/**
 * /admin/users — global Users admin surface (#1023 Slice 2;
 * meta #1021).
 *
 * Mirrors the `/admin/admins` shape: a directory table with a row
 * click to open a detail modal. Filter controls sit above the table;
 * URL params reflect filter + pagination state so deep links work
 * (a workspace-membership debug ticket can paste a URL with
 * `?orgId=abc&role=admin` and land on the relevant view).
 *
 * v1 UI surfaces a subset of the filter primitives the backend
 * supports (search, orphan toggle, provider, sign-in recency). The
 * remainder (`orgId`, `workspaceId`, `role`) are URL-param-only —
 * adding controls later doesn't require a backend change.
 *
 * Detail uses a modal rather than a dedicated `/admin/users/:userId`
 * route. Matches the `Admins` page shape (no detail route either) and
 * keeps the URL state focused on the directory filters. When Tier-2
 * actions land (suspend, impersonate), the modal grows action
 * buttons in-place — no routing change needed.
 *
 * The frontend's `<RequireSuperadmin>` wrapper around `/admin/*` is
 * UX defense-in-depth; the actual data boundary is the server's
 * `requireSession + requireSuperadmin` chain on `/api/admin/users`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  adminUsersAPI,
  type AdminUserDetail,
  type AdminUserListFilter,
  type AdminUserListRow,
} from '@/lib/api'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatLocalDateTime } from '@/lib/utils'

const PAGE_SIZE = 100

/** Sign-in recency presets. Map a label to a "days ago" cutoff that
 * the page translates to a `signedInAfter` / `signedInBefore` URL
 * param. `'any'` clears both. */
const SIGN_IN_RECENCY_OPTIONS = [
  { value: 'any', label: 'Any recency' },
  { value: '7d', label: 'Signed in within 7 days' },
  { value: '30d', label: 'Signed in within 30 days' },
  { value: '90d', label: 'Signed in within 90 days' },
  { value: 'gt30d', label: 'No sign-in for 30+ days' },
  { value: 'gt90d', label: 'No sign-in for 90+ days' },
  { value: 'never', label: 'Never signed in' },
] as const

type SignInRecency = (typeof SIGN_IN_RECENCY_OPTIONS)[number]['value']

/** Provider dropdown shapes. The Better Auth value `credential` covers
 * email+password — we expose it under the friendlier label
 * "Email / password." Other OAuth providers are listed verbatim. */
const PROVIDER_OPTIONS = [
  { value: 'any', label: 'Any provider' },
  { value: 'credential', label: 'Email / password' },
  { value: 'google', label: 'Google' },
  { value: 'github', label: 'GitHub' },
] as const

type ProviderFilter = (typeof PROVIDER_OPTIONS)[number]['value']

interface FilterState {
  search: string
  orphan: boolean
  provider: ProviderFilter
  recency: SignInRecency
}

const EMPTY_FILTER: FilterState = {
  search: '',
  orphan: false,
  provider: 'any',
  recency: 'any',
}

function readFilterFromUrl(params: URLSearchParams): FilterState {
  return {
    search: params.get('search') ?? '',
    orphan: params.get('orphan') === 'true',
    provider: (PROVIDER_OPTIONS.find((o) => o.value === params.get('provider'))?.value ??
      'any') as ProviderFilter,
    recency: (SIGN_IN_RECENCY_OPTIONS.find((o) => o.value === params.get('recency'))?.value ??
      'any') as SignInRecency,
  }
}

function writeFilterToUrl(state: FilterState): Record<string, string> {
  const out: Record<string, string> = {}
  if (state.search.trim().length > 0) out.search = state.search.trim()
  if (state.orphan) out.orphan = 'true'
  if (state.provider !== 'any') out.provider = state.provider
  if (state.recency !== 'any') out.recency = state.recency
  return out
}

/** Translate the user-facing recency option to the underlying
 * `signedInBefore` / `signedInAfter` / `signedIn` API filter
 * primitives. "Never signed in" maps to `signedIn: false` (a
 * server-side primitive added in PR #1036 Copilot review) — the
 * earlier client-side post-filter mis-paginated when matching rows
 * fell outside the current page. */
function recencyToApiFilter(
  recency: SignInRecency,
): Pick<AdminUserListFilter, 'signedInBefore' | 'signedInAfter' | 'signedIn'> {
  const now = new Date()
  const daysAgo = (days: number): Date => {
    const d = new Date(now)
    d.setDate(d.getDate() - days)
    return d
  }
  switch (recency) {
    case '7d':
      return { signedInAfter: daysAgo(7).toISOString() }
    case '30d':
      return { signedInAfter: daysAgo(30).toISOString() }
    case '90d':
      return { signedInAfter: daysAgo(90).toISOString() }
    case 'gt30d':
      return { signedInBefore: daysAgo(30).toISOString() }
    case 'gt90d':
      return { signedInBefore: daysAgo(90).toISOString() }
    case 'never':
      return { signedIn: false }
    default:
      return {}
  }
}

/** Debounce window between the last search-input keystroke and the
 * triggered list fetch. 300ms is the inflection point between
 * "feels instant" (under 250ms) and "feels laggy" (over 400ms).
 * PR #1036 Copilot review: every keystroke previously fired both a
 * list query AND a `user.list_viewed` audit row, so typing a single
 * email could write 20+ audit rows for intermediate strings. The
 * debounce collapses an in-flight typing session into one fetch
 * (and one audit row) per pause. */
const SEARCH_DEBOUNCE_MS = 300

export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tz = useUserTimezone()
  const [filterState, setFilterState] = useState<FilterState>(() =>
    readFilterFromUrl(searchParams),
  )
  /** Live, un-debounced search input. The displayed input value
   * tracks this on every keystroke so typing feels responsive.
   * The debounced effect below copies it into `filterState.search`
   * after the user pauses, which is the value that drives the
   * fetch. */
  const [searchInput, setSearchInput] = useState<string>(
    () => filterState.search,
  )
  const [rows, setRows] = useState<AdminUserListRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [offset, setOffset] = useState(() => {
    const raw = searchParams.get('offset')
    const n = raw ? parseInt(raw, 10) : 0
    return Number.isFinite(n) && n >= 0 ? n : 0
  })
  const [endReached, setEndReached] = useState(false)
  const [detailUserId, setDetailUserId] = useState<string | null>(null)

  // Debounce: copy `searchInput` into `filterState.search` after the
  // user pauses typing. The reload-on-filter-change effect below
  // then fires exactly once per pause rather than once per keystroke.
  useEffect(() => {
    if (searchInput === filterState.search) return
    const handle = setTimeout(() => {
      setOffset(0)
      setFilterState((prev) => ({ ...prev, search: searchInput }))
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchInput, filterState.search])

  const reload = useCallback(
    async (state: FilterState, nextOffset: number): Promise<void> => {
      setLoading(true)
      setLoadError(null)
      try {
        const apiFilter: AdminUserListFilter = {
          ...(state.search.trim().length > 0
            ? { search: state.search.trim() }
            : {}),
          ...(state.orphan ? { orphan: true } : {}),
          ...(state.provider !== 'any' ? { provider: state.provider } : {}),
          ...recencyToApiFilter(state.recency),
          // Pass through `orgId`, `workspaceId`, `role` from URL —
          // not surfaced as v1 controls but URL-param-deep-linkable.
          ...(searchParams.get('orgId')
            ? { orgId: searchParams.get('orgId') ?? undefined }
            : {}),
          ...(searchParams.get('workspaceId')
            ? { workspaceId: searchParams.get('workspaceId') ?? undefined }
            : {}),
          ...(searchParams.get('role')
            ? { role: searchParams.get('role') ?? undefined }
            : {}),
        }
        const result = await adminUsersAPI.list(apiFilter, {
          limit: PAGE_SIZE,
          offset: nextOffset,
        })
        setRows(result.rows)
        setEndReached(result.rows.length < PAGE_SIZE)
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'Failed to load users')
      } finally {
        setLoading(false)
      }
    },
    [searchParams],
  )

  // Sync URL params when filter or pagination changes. `replace: true`
  // so the back button steps over user-mid-typing search snapshots.
  useEffect(() => {
    const urlParams = writeFilterToUrl(filterState)
    if (offset > 0) urlParams.offset = String(offset)
    // Preserve URL-param-only filters that the UI doesn't expose.
    for (const k of ['orgId', 'workspaceId', 'role']) {
      const v = searchParams.get(k)
      if (v) urlParams[k] = v
    }
    setSearchParams(urlParams, { replace: true })
    void reload(filterState, offset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState, offset])

  // Initial load — `useEffect` above also covers this (it fires on
  // mount with the initial state), so no explicit mount-only effect
  // is needed. Search bar is debounced manually below.

  function handleSearchChange(value: string): void {
    // Live UI update only — the debounce effect above promotes the
    // value into `filterState.search` after the user pauses, which
    // is what triggers the fetch + audit row.
    setSearchInput(value)
  }
  function handleOrphanToggle(checked: boolean): void {
    setOffset(0)
    setFilterState((prev) => ({ ...prev, orphan: checked }))
  }
  function handleProviderChange(value: string): void {
    setOffset(0)
    setFilterState((prev) => ({ ...prev, provider: value as ProviderFilter }))
  }
  function handleRecencyChange(value: string): void {
    setOffset(0)
    setFilterState((prev) => ({ ...prev, recency: value as SignInRecency }))
  }
  function handleClearFilters(): void {
    setOffset(0)
    setSearchInput('')
    setFilterState(EMPTY_FILTER)
    // Clear URL-param-only filters too.
    const next = new URLSearchParams(searchParams)
    for (const k of ['orgId', 'workspaceId', 'role']) next.delete(k)
    setSearchParams(next, { replace: true })
  }

  const totalActiveFilters = useMemo(() => {
    let n = 0
    if (filterState.search.trim().length > 0) n += 1
    if (filterState.orphan) n += 1
    if (filterState.provider !== 'any') n += 1
    if (filterState.recency !== 'any') n += 1
    for (const k of ['orgId', 'workspaceId', 'role']) {
      if (searchParams.get(k)) n += 1
    }
    return n
  }, [filterState, searchParams])

  return (
    <div className="space-y-6 max-w-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every user who has signed up to OttoTester. Click a row to
            see the user's full org and workspace membership graph. You
            can delete a user who belongs to no organizations; suspension,
            force-logout, and impersonation land in a later slice.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="users-search">Search email or name</Label>
              <Input
                id="users-search"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="substring match"
                clearable
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="users-provider">Provider</Label>
              <Select
                id="users-provider"
                value={filterState.provider}
                onChange={(e) => handleProviderChange(e.target.value)}
                options={PROVIDER_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="users-recency">Sign-in recency</Label>
              <Select
                id="users-recency"
                value={filterState.recency}
                onChange={(e) => handleRecencyChange(e.target.value)}
                options={SIGN_IN_RECENCY_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox
                id="users-orphan"
                checked={filterState.orphan}
                onCheckedChange={(c) => handleOrphanToggle(c === true)}
              />
              <Label htmlFor="users-orphan" className="text-sm">
                Only users with zero org memberships (orphans)
              </Label>
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-2">
              {totalActiveFilters > 0 && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  Clear {totalActiveFilters} filter{totalActiveFilters === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Users
            {rows && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({rows.length} on this page)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadError && (
            <p className="text-sm text-destructive" role="alert">
              {loadError}
            </p>
          )}

          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead>Primary org</TableHead>
                  <TableHead className="text-right">Memberships</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && !rows && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                      Loading users…
                    </TableCell>
                  </TableRow>
                )}
                {rows?.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => setDetailUserId(row.id)}
                    className="cursor-pointer hover:bg-muted/30"
                  >
                    <TableCell className="font-medium">
                      {row.email}
                      {row.isOrphan && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Orphan
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{row.name ?? <span className="text-muted-foreground italic">—</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.createdAt ? formatLocalDateTime(row.createdAt, tz) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.lastSignInAt ? (
                        formatLocalDateTime(row.lastSignInAt, tz)
                      ) : (
                        <span className="italic">Never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.primaryOrg?.name ?? <span className="text-muted-foreground italic">—</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {row.orgMembershipCount}
                    </TableCell>
                  </TableRow>
                ))}
                {rows && rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                      No users match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Showing rows {offset + 1}-{offset + (rows?.length ?? 0)}
              {endReached && rows && rows.length > 0 && ' (end of feed)'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || loading}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={endReached || loading}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <UserDetailDialog
        userId={detailUserId}
        onOpenChange={(open) => {
          if (!open) setDetailUserId(null)
        }}
        onDeleted={() => {
          // Close the modal and refresh the list so the deleted user
          // drops off immediately.
          setDetailUserId(null)
          void reload(filterState, offset)
        }}
      />
    </div>
  )
}

// ── Detail modal ────────────────────────────────────────────────────

function UserDetailDialog({
  userId,
  onOpenChange,
  onDeleted,
}: {
  userId: string | null
  onOpenChange: (open: boolean) => void
  /** Called after a successful delete so the parent can close the modal
   * and refresh the list. */
  onDeleted: () => void
}) {
  const tz = useUserTimezone()
  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Delete flow (#1542). Only an orphan (zero org memberships) who is
  // not a superadmin can be deleted; the server re-checks both.
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setDetail(null)
      setError(null)
      setConfirmOpen(false)
      setDeleteError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    adminUsersAPI
      .detail(userId)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load detail')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  const open = userId !== null

  // Delete is offered only for an orphan (no org memberships) who is
  // not a superadmin. Mirrors the server's refusal rules so the UI
  // never offers an action the API would reject.
  const isOrphan = detail !== null && detail.orgs.length === 0
  const canDelete = isOrphan && detail !== null && !detail.isSuperadmin

  async function handleConfirmDelete(): Promise<void> {
    if (!userId) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await adminUsersAPI.delete(userId)
      setConfirmOpen(false)
      onDeleted()
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{detail?.email ?? (loading ? 'Loading user…' : 'User detail')}</DialogTitle>
          <DialogDescription>
            {detail?.name ?? null}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-6">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          {detail && (
            <>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Profile
                </h3>
                <dl className="grid grid-cols-2 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">User id</dt>
                  <dd className="font-mono text-xs">{detail.id}</dd>
                  <dt className="text-muted-foreground">Email verified</dt>
                  <dd>{detail.emailVerified ? 'Yes' : 'No'}</dd>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd>
                    {detail.createdAt
                      ? formatLocalDateTime(detail.createdAt, tz)
                      : '—'}
                  </dd>
                  <dt className="text-muted-foreground">Last sign-in</dt>
                  <dd>
                    {detail.lastSignInAt
                      ? formatLocalDateTime(detail.lastSignInAt, tz)
                      : 'Never'}
                  </dd>
                  <dt className="text-muted-foreground">Providers</dt>
                  <dd>
                    {detail.providers.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {detail.providers.map((p) => (
                          <Badge key={p} variant="secondary" className="text-xs">
                            {p === 'credential' ? 'Email / password' : p}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic">None</span>
                    )}
                  </dd>
                  <dt className="text-muted-foreground">Global Admin</dt>
                  <dd>
                    {detail.isSuperadmin ? (
                      <Badge variant="default">Yes</Badge>
                    ) : (
                      'No'
                    )}
                  </dd>
                  <dt className="text-muted-foreground">Suspended</dt>
                  <dd>
                    {detail.suspended ? (
                      <Badge variant="destructive">Yes</Badge>
                    ) : (
                      'No'
                    )}
                  </dd>
                </dl>
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Organizations ({detail.orgs.length})
                </h3>
                {detail.orgs.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No org memberships (orphan account).
                  </p>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Org</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detail.orgs.map((o) => (
                          <TableRow key={o.orgId}>
                            <TableCell className="font-medium">{o.orgName}</TableCell>
                            <TableCell className="text-sm">{o.role}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Workspaces ({detail.workspaces.length})
                </h3>
                {detail.workspaces.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No direct workspace memberships.
                  </p>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Workspace</TableHead>
                          <TableHead>Org</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detail.workspaces.map((w) => (
                          <TableRow key={w.workspaceId}>
                            <TableCell className="font-medium">{w.workspaceName}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {w.orgName}
                            </TableCell>
                            <TableCell className="text-sm">{w.role}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </section>
            </>
          )}
        </DialogBody>
        {detail && (
          <DialogFooter className="items-center justify-between sm:justify-between">
            {canDelete ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setDeleteError(null)
                  setConfirmOpen(true)
                }}
              >
                Delete user
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                {!isOrphan
                  ? 'This user belongs to organizations — remove them from those first.'
                  : 'Revoke this user’s admin access first.'}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>

      {/* Confirm — names the consequence in plain language. */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => !deleting && setConfirmOpen(o)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this user?</DialogTitle>
            <DialogDescription>
              {detail?.email} belongs to no organizations, so deleting them
              removes only their sign-in records (account, sessions, and any
              access tokens).
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Their entries in the Audit Log are kept. This can’t be undone.
            </p>
            {deleteError && (
              <p className="text-sm text-destructive" role="alert">
                {deleteError}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}

export default UsersPage
